/* eslint-disable react/jsx-handler-names */
import PropType from "prop-types";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { CaretRightIcon, UserCircleIcon } from "@phosphor-icons/react";
import PopOver from "../../../../atoms/popOver";
import CustomImage from "../../../../atoms/customImage";
import { desktopIndiaConfig } from "../../../../../constants/mockData/mockData";
import { useDispatch, useSelector } from "react-redux";
import CurrencyList from "./currencyList";
import { constantText, headerAccountDropdown } from "../../../../../constants/common/headerToolbarConstants";
import { requestLogout, requestToOpenLoginDrawer } from "../../../../../store/slices/loginSignupSlice";
import { setHeaderAccountLoginRedirect } from "../../../../../utils/login/headerAccountLoginRedirect";
import { useState } from "react";
import MediaPopup from "../../mediaPopup";
import { LOG_OUT } from "../../../../../api/cartApi/constant";
import { lockBody } from "../../../../../utils/document";
import { cleverTapCommonEvents } from "../../../../../analytics/clevertapConstants";
import useAnalytics from "../../../../../analytics/useAnalytics";
import CookieUtil from "../../../../../utils/storage/cookieUtil";
import { HDFC_SMARTBUY, PARTNER } from "../../../../../constants/storage";
import PropTypes from "prop-types";
import { clearAccountClientSession } from "../../../../../utils/myAccount/logout";
import AccountMenuPhosphorIcon from "../../../../../containers/myAccountPage/components/AccountMenuPhosphorIcon";
import { ACCOUNT_MENU_ITEM_IDS } from "../../../../../constants/myAccount/menu";

/**
 * @param {string | undefined} fullName
 * @returns {string}
 */
function getAccountInitial(fullName) {
  const trimmed = fullName?.trim();
  if (!trimmed) {
    return "";
  }
  return trimmed.charAt(0).toUpperCase();
}

/** */
const HeaderDropDownList = ({ activeTab, setSelectedCurrency, selectedCurrency, commonData, setActiveTab }) => {
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const router = useRouter();
  const loginDetail = useSelector((state) => state.fus.loginDetail);
  const checkLoginStatus = loginDetail?.loggedin;
  const fnpCashTitle = useSelector((state) => state.fnpCash?.fnpCashTitle);
  const [isShowMedia, setIsShowMedia] = useState(false);
  const [isDropdownVisible, setIsDropdownVisible] = useState(true);
  const isSmartBuyUser = CookieUtil?.getCookie(PARTNER) === HDFC_SMARTBUY;

  const pageHeaderMenuData = {
    ...commonData,
    menu_name: null,
  };
  const accountPopoverClass =
    "absolute cursor-pointer right-0 z-6 w-[286px] overflow-hidden rounded-[16px] border border-thinLightGrey bg-white-900 shadow-[0px_12px_40px_rgba(25,26,11,0.08)] scrollbar-webkit";
  const popoverClasses = {
    moreItems: accountPopoverClass,
    account: accountPopoverClass,
    currency: "absolute cursor-pointer h-[373px] w-[212px] overflow-auto bg-white-900 scrollbar-thin scrollbar-webkit",
  };
  const popoverParentClasses = {
    moreItems: "absolute top-[calc(100%+4px)] -right-16 min-w-[286px]",
    account: "absolute top-[calc(100%+4px)] -right-16 min-w-[286px]",
    currency: "absolute top-[calc(100%+4px)] -left-[85px]",
  };

  /** Handle hiding dropdown when currency is selected */
  const handleCurrencySelect = (currency) => {
    setActiveTab(null);
    setSelectedCurrency(currency);
    setIsDropdownVisible(false);
  };

  /**
   * @param {object} item
   */
  const trackMenuClick = (item) => {
    trackEvent(
      cleverTapCommonEvents.headerMenuClicked,
      { ...pageHeaderMenuData, menu_name: item?.label ?? item?.text },
      ["clevertap"],
    );
  };

  /**
   * @param {object} item
   * @returns {boolean}
   */
  const isAccountRouteMenuItem = (item) => Boolean(item?.landingUrl?.startsWith("/account"));

  /**
   * Opens login for guests (desktop account menu).
   * @param {object} item
   * @param {string | null} [redirectPath]
   */
  const openGuestLogin = (item, redirectPath = null) => {
    trackMenuClick(item);
    setActiveTab(null);
    setHeaderAccountLoginRedirect(redirectPath);
    dispatch(requestToOpenLoginDrawer(true));
    lockBody();
    setIsDropdownVisible(false);
  };

  /**
   * This function for handling click on dropdowns list items.
   *
   * @function
   * @param {object} item
   * @returns {void}
   */
  const onDropdownItemClick = (item) => {
    setActiveTab(null);
    if (item?.action === "login" || item?.text === constantText.LOGIN_REGISTER) {
      trackMenuClick(item);
      setHeaderAccountLoginRedirect(null);
      dispatch(requestToOpenLoginDrawer(true));
      lockBody();
    } else if (item?.text === constantText.MEDIA) {
      trackMenuClick(item);
      setIsShowMedia(!isShowMedia);
    } else if (item?.action === "logout" || item?.menuId === ACCOUNT_MENU_ITEM_IDS.LOGOUT) {
      if (typeof window?.clearAndDestoryWidget === "function") {
        window?.clearAndDestoryWidget();
      }
      delete window.userDetails;
      trackMenuClick(item);
      clearAccountClientSession();
      dispatch(requestLogout());
      router.push(LOG_OUT());
    }
    setIsDropdownVisible(false);
  };

  /**
   * @param {object} item
   * @returns {string}
   */
  const getItemLabel = (item) => {
    if (item?.label === "fnpCash") {
      const title = fnpCashTitle || "FNP Cash";
      if (!title || title.toLowerCase() === "fnpcash") {
        return "FNP Cash";
      }
      return title;
    }
    return item?.label ?? item?.text ?? "";
  };

  /**
   * @param {object} item
   * @param {boolean} [isLogoutRow]
   * @returns {import("react").ReactNode}
   */
  const renderAccountMenuRow = (item, isLogoutRow = false) => {
    const label = getItemLabel(item);
    const showChevron = !isLogoutRow && (item?.landingUrl || item?.action === "login");

    const rowInner = (
      <>
        <AccountMenuPhosphorIcon menuId={item.menuId} size={20} className="shrink-0 text-fnp-500" />
        <span className={`min-w-0 flex-1 text-12 leading-4 ${isLogoutRow ? "text-grey-400" : "text-fnp-500"}`}>
          {label}
        </span>
        {showChevron ? <CaretRightIcon size={16} className="shrink-0 text-grey-400" aria-hidden={true} /> : null}
      </>
    );

    const rowClassName = `grid min-h-[44px] grid-cols-[20px_1fr_16px] items-center gap-8 rounded-[10px] px-12 hover:bg-grey-100 ${
      isLogoutRow ? "border-t border-thinLightGrey pt-4 rounded-none" : ""
    }`;

    if (item?.landingUrl) {
      if (!checkLoginStatus && isAccountRouteMenuItem(item)) {
        return (
          <button
            key={item.menuId}
            type="button"
            className={`${rowClassName} w-full text-left`}
            onClick={() => openGuestLogin(item, item.landingUrl)}
          >
            {rowInner}
          </button>
        );
      }

      return (
        <Link key={item.menuId} href={item.landingUrl} className={rowClassName} onClick={() => trackMenuClick(item)}>
          {rowInner}
        </Link>
      );
    }

    return (
      <button
        key={item.menuId}
        type="button"
        className={`${rowClassName} w-full text-left`}
        onClick={() => onDropdownItemClick(item)}
      >
        {rowInner}
      </button>
    );
  };

  /**
   * @param {object[]} items
   * @param {{ isLogout?: boolean }} [options]
   * @returns {import("react").ReactNode}
   */
  const renderAccountMenuSection = (items, options = {}) => {
    return items
      ?.filter((item) => !(isSmartBuyUser && item.hideForSmartBuy))
      .map((item) => renderAccountMenuRow(item, options.isLogout));
  };

  /**
   * @returns {import("react").ReactNode}
   */
  const renderAccountPopover = () => {
    const displayName = loginDetail?.fullname?.trim() || "Guest";
    const initial = getAccountInitial(loginDetail?.fullname);
    const accountPrimaryMenuItems = checkLoginStatus
      ? headerAccountDropdown.loggedInPrimary
      : [headerAccountDropdown.guestLoginMenuItem, ...headerAccountDropdown.loggedInPrimary];

    return (
      <div className="flex flex-col">
        <div className="flex items-center gap-12 border-b border-thinLightGrey bg-[#f7f7ee] p-16">
          {checkLoginStatus ? (
            <>
              <span className="flex size-[40px] shrink-0 items-center justify-center rounded-full bg-fnp-500 text-14 font-600 leading-5 text-white-900">
                {initial}
              </span>
              <div>
                <p className="text-10 leading-4 text-grey-400">{constantText.SIGNED_IN_AS}</p>
                <p className="text-14 font-600 leading-5 text-fnp-500">{displayName}</p>
              </div>
            </>
          ) : (
            <>
              <span className="flex size-[40px] shrink-0 items-center justify-center rounded-full bg-white-900">
                <UserCircleIcon size={28} weight="regular" className="text-fnp-500" aria-hidden={true} />
              </span>
              <p className="text-14 font-600 leading-5 text-fnp-500">{constantText.HELLO_GUEST}</p>
            </>
          )}
        </div>
        <div className="flex flex-col p-8">
          {renderAccountMenuSection(accountPrimaryMenuItems)}
          <div className="mx-12 my-6 h-px bg-[#EEEEEE]" aria-hidden={true} />
          {renderAccountMenuSection(headerAccountDropdown.loggedInSupport)}
          {checkLoginStatus ? (
            <div className="pt-4">
              {renderAccountMenuSection(headerAccountDropdown.loggedInFooter, { isLogout: true })}
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  /**
   * Renders the popover content based on the provided items and textKey.
   *
   * @function renderPopoverContent
   * @param {Array} items - The list of items to render.
   * @param {string} textKey - The key used to determine the type of content to render.
   * @returns {JSX.Element} The rendered content for the popover.
   */
  const renderPopoverContent = (items, textKey) => {
    if (textKey === "countryCurrency") {
      return <CurrencyList setSelectedCurrency={handleCurrencySelect} selectedCurrency={selectedCurrency} />;
    }

    const rowClassName =
      "grid min-h-[44px] w-full grid-cols-[20px_1fr_16px] items-center gap-8 rounded-[10px] px-12 text-left hover:bg-grey-100";

    return (
      <div className="flex flex-col p-8">
        {items?.map((item, idx) => {
          const label = item[textKey];
          const showChevron = Boolean(item?.landingUrl);
          const rowInner = (
            <>
              <CustomImage
                src={item?.iconUrl}
                alt=""
                sizes="20px"
                width={20}
                height={20}
                isPriority={false}
                className="size-[20px] shrink-0 object-contain"
              />
              <span className="min-w-0 flex-1 text-12 leading-4 text-fnp-500">{label}</span>
              {showChevron ? <CaretRightIcon size={16} className="shrink-0 text-grey-400" aria-hidden={true} /> : null}
            </>
          );

          if (item?.landingUrl) {
            return (
              <Link key={idx} href={item.landingUrl} className={rowClassName} onClick={() => trackMenuClick(item)}>
                {rowInner}
              </Link>
            );
          }

          return (
            <button key={idx} type="button" className={rowClassName} onClick={() => onDropdownItemClick(item)}>
              {rowInner}
            </button>
          );
        })}
      </div>
    );
  };

  const popoverContent = {
    moreItems: renderPopoverContent(desktopIndiaConfig?.moreInfoDropDown, "text"),
    account: renderAccountPopover(),
    currency: renderPopoverContent(null, "countryCurrency"),
  };

  /** */
  const onToggleMediaPopup = () => {
    setIsShowMedia(!isShowMedia);
  };
  return isShowMedia ? (
    <MediaPopup onToggleMediaPopup={onToggleMediaPopup} />
  ) : (
    isDropdownVisible && (
      <PopOver
        isVisible={true}
        isShowPageScroll={true}
        classes={popoverClasses[activeTab]}
        parentClass={popoverParentClasses[activeTab]}
      >
        {popoverContent[activeTab]}
      </PopOver>
    )
  );
};
HeaderDropDownList.propTypes = {
  activeTab: PropType.string.isRequired,
  setSelectedCurrency: PropType.func.isRequired,
  selectedCurrency: PropType.string.isRequired,
  commonData: PropTypes.object,
  setActiveTab: PropTypes.func.isRequired,
};
export default HeaderDropDownList;
